/**
 * Gemini AI Service (Frontend)
 *
 * All AI API calls are proxied through the Bun.js backend (/api/gemini/*).
 * The Gemini API key is NEVER exposed to the browser.
 *
 * This service handles:
 * - Prompt building (Conteúdo Notável methodology, document context, YouTube/Instagram)
 * - Client-side document processing (PDF, TXT, MD)
 * - Calling backend endpoints for actual AI generation
 *
 * SECURITY: API keys live server-side only.
 */

import { Slide, SlideType, AspectRatio, UploadedDocument, Language, AnalisePreCriacao, ChecklistValidacao, AvatarData, GeneratedAvatarResult } from '../types';
import {
  extractInstagramUrls,
  formatInstagramContentForAI,
  scrapeInstagramPost,
} from './instagramService';
import { apiPost } from './apiClient';
import { applyImageDistributionRules } from './imageDistributionRules';
import { generateId } from '@/lib/utils';

// ============================================================================
// MODEL CONSTANTS (for display/UI only - actual models are configured server-side)
// ============================================================================

// Claude text models (primary for text generation)
export const CLAUDE_MODEL_SONNET = 'claude-sonnet-4-6';
export const CLAUDE_MODEL_HAIKU = 'claude-haiku-4-5';

// Gemini text models (kept for backward compatibility)
export const TEXT_MODEL_PRO = 'gemini-2.5-pro';
export const TEXT_MODEL_FLASH = 'gemini-2.5-flash';

// Gemini image models (Gemini is the only provider for images)
export const IMAGE_MODEL_PRO = 'gemini-3-pro-image-preview';
export const IMAGE_MODEL_FLASH = 'gemini-2.5-flash-image';

// ============================================================================
// YOUTUBE URL DETECTION
// ============================================================================

const YOUTUBE_URL_REGEX = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)[a-zA-Z0-9_-]{11}/g;

export const extractYouTubeUrls = (text: string): string[] => {
  const matches = text.match(YOUTUBE_URL_REGEX);
  return matches || [];
};

const normalizeYouTubeUrl = (url: string): string => {
  let normalized = url;
  if (!normalized.startsWith('http')) normalized = `https://${normalized}`;

  // Extract video ID and always return canonical format
  // Gemini fileUri works reliably with youtube.com/watch?v= but may fail with youtu.be/
  const shortMatch = normalized.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (shortMatch) {
    return `https://www.youtube.com/watch?v=${shortMatch[1]}`;
  }
  const shortsMatch = normalized.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  if (shortsMatch) {
    return `https://www.youtube.com/watch?v=${shortsMatch[1]}`;
  }

  // Standard format — strip any extra params (si=, t=, etc.) keeping only v=
  const idMatch = normalized.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  if (idMatch) {
    return `https://www.youtube.com/watch?v=${idMatch[1]}`;
  }

  return normalized;
};

// ============================================================================
// ASPECT RATIO MAPPING
// ============================================================================

export const getApiAspectRatio = (ratio: AspectRatio): string => {
  // Gemini supported ratios: 1:1, 2:3, 3:2, 3:4, 4:3, 9:16, 16:9, 21:9
  // 4:5 is NOT supported, map to 3:4 (closest supported ratio)
  const mapping: Record<AspectRatio, string> = {
    '1/1': '1:1',
    '4/5': '3:4',
    '9/16': '9:16',
    '16/9': '16:9'
  };
  return mapping[ratio] || '1:1';
};

// ============================================================================
// DOCUMENT PROCESSING (client-side only)
// ============================================================================

export const processDocument = async (file: File): Promise<UploadedDocument> => {
  const extension = file.name.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return await processPdf(file);
    case 'txt':
    case 'md':
      return await processTextFile(file, extension as 'txt' | 'md');
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'heic':
      return await processImage(file);
    default:
      throw new Error(`Unsupported file type: ${extension}`);
  }
};

const processPdf = async (file: File): Promise<UploadedDocument> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const base64 = dataUrl.split(',')[1];
      resolve({
        name: file.name,
        type: 'pdf',
        content: '',
        base64,
        mimeType: 'application/pdf',
        size: file.size
      });
    };
    reader.onerror = () => reject(new Error('Failed to read PDF file'));
    reader.readAsDataURL(file);
  });
};

const processImage = async (file: File): Promise<UploadedDocument> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const base64 = dataUrl.split(',')[1];
      resolve({
        name: file.name,
        type: 'image',
        content: '',
        base64,
        mimeType: file.type || 'image/jpeg',
        size: file.size
      });
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
};

const processTextFile = async (file: File, type: 'txt' | 'md'): Promise<UploadedDocument> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve({
        name: file.name,
        type,
        content: e.target?.result as string,
        size: file.size
      });
    };
    reader.onerror = () => reject(new Error('Failed to read text file'));
    reader.readAsText(file);
  });
};

// ============================================================================
// LANGUAGE INSTRUCTIONS
// ============================================================================

const LANGUAGE_INSTRUCTIONS: Record<Language, string> = {
  'pt-BR': 'CRITICAL LANGUAGE REQUIREMENT: Write ALL slide content in Brazilian Portuguese (PT-BR). Every single slide — titles, body text, CTA — MUST be written in Portuguese. Do NOT use English for any slide content.',
  'en': 'CRITICAL LANGUAGE REQUIREMENT: Write ALL slide content in English. Every single slide — titles, body text, CTA — MUST be written in English.',
  'es': 'CRITICAL LANGUAGE REQUIREMENT: Write ALL slide content in Spanish (Español). Every single slide — titles, body text, CTA — MUST be written in Spanish. Do NOT use English for any slide content.',
};

// ============================================================================
// TEXT GENERATION (via Backend)
// ============================================================================

interface GeneratedSlideSchema {
  /**
   * Stable id assigned by the server when it persists the carousel inline
   * (chat-first flow). Absent for legacy clients that don't use the `save`
   * block — those still generate ids locally.
   */
  id?: string;
  type: string;
  content: string;
  suggestedImagePrompt: string;
  needsImage: boolean;
  // Conteúdo Notável metadata
  gatilhos_usados?: string[];
  elementos_usados?: string[];
  tipo_cta?: string;
}

interface GeneratedCarouselResponse {
  format?: 'narrative' | 'list';
  slides: GeneratedSlideSchema[];
  analise_pre_criacao: {
    desejo_principal: string;
    dor_especifica: string;
    crenca_desafiada: string;
    informacao_nova: string;
    moeda_social_ativada: string;
    pontos_identificacao: string[];
  };
  checklist_validacao: {
    informacao_nova: boolean;
    baixo_carregamento_cognitivo: boolean;
    moeda_social: boolean;
    brevidade_inteligente: boolean;
    pontos_identificacao_especificos: boolean;
    valor_pratico: boolean;
    fluxo_natural: boolean;
    cover_irresistivel: boolean;
    cta_conectado: boolean;
  };
}

/** Result of AI carousel generation including metadata */
export interface CarouselGenerationResult {
  slides: Slide[];
  format: 'narrative' | 'list';
  analisePreCriacao: AnalisePreCriacao;
  checklistValidacao: ChecklistValidacao;
  /**
   * Present only when `generateCarouselContent` was called with a `save`
   * option: the server persisted the project atomically with generation,
   * so the client can skip its initial save call.
   */
  savedProject?: { id: string; name: string };
}

/** Optional save block — when passed, server persists the project inline. */
export interface CarouselGenerationSaveOptions {
  projectId: string;
  style: string;
  aspectRatio: string;
  profile: Record<string, any>;
  language?: string;
  isDemo?: boolean;
}

// ============================================================================
// CONTENT SUGGESTIONS TYPES
// ============================================================================

export interface ContentSuggestion {
  title: string;
  description: string;
  topic: string;
}

export interface CachedSuggestions {
  suggestions: ContentSuggestion[];
  generatedAt: string; // ISO timestamp
  avatarHash?: string; // simple hash to detect avatar changes (Gap 4)
}

// ============================================================================
// AVATAR FORMATTING
// ============================================================================

/**
 * Converts structured AvatarData into a formatted text string for AI prompts.
 * This text is injected into the "Conteúdo Notável" prompt as avatarContext.
 */
export const formatAvatarForPrompt = (avatar: AvatarData): string => {
  // Prefer magnetic bio + AI-generated structured data
  if (avatar.magneticBio?.trim()) {
    const parts = [avatar.magneticBio.trim()];
    if (avatar.publicoAlvo) parts.push(`Público-alvo: ${avatar.publicoAlvo}`);
    if (avatar.doresIdentificadas?.length) parts.push(`Dores: ${avatar.doresIdentificadas.join(', ')}`);
    if (avatar.desejosIdentificados?.length) parts.push(`Desejos: ${avatar.desejosIdentificados.join(', ')}`);
    if (avatar.posicionamento) parts.push(`Posicionamento: ${avatar.posicionamento}`);
    if (avatar.metodoAbordagem) parts.push(`Método: ${avatar.metodoAbordagem}`);
    return parts.join('\n');
  }

  // Fallback to simple bioDescription
  if (avatar.bioDescription?.trim()) {
    return avatar.bioDescription.trim();
  }

  // Fallback to legacy structured fields
  const lines: string[] = [];
  if (avatar.nicho) lines.push(`Nicho: ${avatar.nicho}`);
  if (avatar.demografico) lines.push(`Público-alvo: ${avatar.demografico}`);
  if (avatar.dores) lines.push(`Dores: ${avatar.dores}`);
  if (avatar.desejos) lines.push(`Desejos: ${avatar.desejos}`);
  if (avatar.crencas) lines.push(`Crenças limitantes: ${avatar.crencas}`);
  if (avatar.objecoes) lines.push(`Objeções: ${avatar.objecoes}`);
  if (avatar.medos) lines.push(`Medos: ${avatar.medos}`);
  if (avatar.momentoDeVida) lines.push(`Momento de vida: ${avatar.momentoDeVida}`);
  return lines.join('\n');
};

// ============================================================================
// MAGNETIC BIO / AVATAR GENERATION (via Backend)
// ============================================================================

/**
 * Sends the user's free-form description to the backend AI to generate
 * a magnetic bio and structured avatar profile.
 */
export const generateMagneticBio = async (
  description: string,
  model?: string
): Promise<GeneratedAvatarResult> => {
  return apiPost<GeneratedAvatarResult>('/api/ai/generate-avatar', {
    description,
    model,
  });
};

// ============================================================================
// CONTENT SUGGESTIONS (via Backend)
// ============================================================================

/**
 * Generates personalized content suggestions based on the user's avatar data.
 * Calls the backend endpoint which handles model selection (Claude primary, Gemini fallback).
 */
export const generateContentSuggestions = async (
  avatarData: AvatarData
): Promise<ContentSuggestion[]> => {
  const avatarPrompt = formatAvatarForPrompt(avatarData);

  if (!avatarPrompt || avatarPrompt.trim().length < 10) {
    throw new Error('Dados do público-alvo insuficientes para gerar sugestões.');
  }

  const response = await apiPost<{
    suggestions: ContentSuggestion[];
    model: string;
    durationMs: number;
  }>('/api/ai/content-suggestions', {
    avatarPrompt,
  });

  return response.suggestions || [];
};

// ============================================================================
// PROMPT BUILDING
// ============================================================================

/**
 * Builds the "Conteúdo Notável" methodology prompt for carousel content generation.
 * Incorporates: 4 mandatory principles, 7 attention triggers, 3-act storytelling,
 * avatar research variables, and expanded JSON output.
 */
const buildCarouselPrompt = (
  topic: string,
  count: number,
  document?: UploadedDocument,
  language: Language = 'pt-BR',
  avatarContext?: string
): string => {
  // For PDFs, content is empty but document exists as base64 visual data
  // For TXT/MD, content contains the extracted text
  const hasDocument = !!document;
  const hasTextContent = hasDocument && !!document.content;

  const langInstruction = LANGUAGE_INSTRUCTIONS[language] || LANGUAGE_INSTRUCTIONS['pt-BR'];

  const avatarSection = avatarContext?.trim()
    ? `\n## PÚBLICO-ALVO / AVATAR\n${avatarContext.trim()}\n`
    : '';

  // Build document/source section separately from topic
  const documentSection = hasDocument
    ? `
## FONTE / DOCUMENTO DE REFERÊNCIA (PRIORIDADE MÁXIMA)

**ATENÇÃO: O conteúdo abaixo é a FONTE PRINCIPAL para a criação do carrossel.**
Você DEVE extrair as informações, insights e argumentos diretamente deste material.
NÃO invente conteúdo que não esteja alinhado com a fonte fornecida.
Use a fonte como base e aplique a metodologia de Conteúdo Notável para torná-la viral.
${topic ? `\nContexto/direcionamento adicional do usuário: "${topic}"\n` : ''}
${hasTextContent
      ? `--- INÍCIO DO DOCUMENTO ---\n${document!.content}\n--- FIM DO DOCUMENTO ---`
      : document!.type === 'image'
        ? `Uma imagem foi fornecida (foto de página/trecho de livro ou texto). Analise visualmente o conteúdo da imagem, extraia TODO o texto visível, e use-o como base para criar o carrossel.`
        : `Um documento PDF foi fornecido como imagem. Analise visualmente todo o conteúdo do PDF fornecido e extraia as informações principais para criar o carrossel.`
    }
`
    : '';

  const topicDisplay = hasDocument
    ? (topic || document!.name || 'o documento fornecido')
    : topic;

  // Dynamic slide range calculations
  const firstContentEnd = Math.ceil((count - 2) * 0.3) + 1;
  const middleContentStart = firstContentEnd + 1;
  const middleContentEnd = count - 2;
  const penultimateSlide = count - 1;

  return `
${langInstruction}

## CONTEXTO DA TAREFA
Você é um especialista em criação de conteúdo viral para Instagram, treinado na metodologia de **Conteúdo Notável** e **Gatilhos da Atenção**.

Sua missão: criar um carrossel de ${count} slides sobre o tema "${topicDisplay}" seguindo rigorosamente a metodologia abaixo.
${avatarSection}
${documentSection}
${hasDocument ? '**LEMBRETE: Baseie TODO o conteúdo dos slides nas informações da fonte/documento fornecido acima. A metodologia abaixo é para ESTRUTURAR e FORMATAR o conteúdo da fonte, não para substituí-lo.**\n' : ''}

---

## OS 4 PRINCÍPIOS OBRIGATÓRIOS (BASE DO CONTEÚDO NOTÁVEL)

Todo carrossel DEVE obrigatoriamente conter:

### 1. INFORMAÇÃO NOVA, POUCO CONHECIDA OU CONTRADITÓRIA
- Traga algo que o público não sabia (efeito surpresa)
- OU desafie crenças populares (contradição que gera debate)
- Exemplo: "Carboidratos à noite NÃO causam ganho de peso"

### 2. BAIXO CARREGAMENTO COGNITIVO
- Use linguagem do público-alvo (sem termos técnicos ou com explicação breve)
- Texto fácil de absorver (como se estivesse conversando)
- Minimize carregamento extrínseco: nada de texto confuso, erros ou excesso de efeitos
- Maximize carregamento germânico: estimule reflexão e ação

### 3. MOEDA SOCIAL
Pergunte-se: "Isso faz o público parecer mais inteligente, atualizado ou inspirado?"

**Ative uma dessas categorias:**
- **Saber raro**: "Quero parecer mais inteligente" (curiosidades, hacks)
- **Identidade**: "Quero mostrar quem sou" (valores, causas)
- **Auto expressão emocional**: "Isso é muito eu" (dor, amor, propósito)
- **Utilidade**: "Quero ser útil" (dicas práticas, tutoriais)
- **Entretenimento**: "Quero fazer rir" (memes, sátiras)
- **Pertencimento tribal**: "Quero me sentir parte do grupo" (bordões, "nós contra eles")

**Técnicas:**
- Use linguagem espelho (fale como o público fala)
- Desperte emoções transferíveis (inspiração, riso, alívio, orgulho)
- Construa frases-bandeira que representem tribos

### 4. BREVIDADE INTELIGENTE
- Transmita MÁXIMO de valor com MÍNIMO de palavras
- Concisão + Clareza + Impacto
- Exemplo: "Você cuida de tudo. Menos de você." (ao invés de "Você vive tão ocupada tentando dar conta de tudo...")

---

## ESTRUTURA DE STORYTELLING (3 ATOS)

### ATO 1 - GANCHO E SITUAÇÃO (Slide 1)
**COVER - CAPTURA DE ATENÇÃO**
- Use NO MÍNIMO 2 dos 7 Gatilhos da Atenção abaixo:

**OS 7 GATILHOS DA ATENÇÃO:**
1. **Atenção Imediata**: Estímulos visuais/sonoros que quebram padrão
2. **Recompensa**: Promessa clara de benefício (desejo + abordagem nova)
3. **Reconhecimento**: Descreva a dor/situação melhor que o próprio público
4. **Crença**: Desafie algo que parece certo OU afirme algo que parece errado
5. **Popularidade/Autoridade**: Use figuras conhecidas, marcas, estudos
6. **Mistério**: Crie lacuna cognitiva ("Isso estava na Bíblia e ninguém viu")
7. **Disrupção**: Quebre expectativa ("Pastor ensinando libertação que está endemoniado")

**FÓRMULA DO COVER:**
- Power words: "Por que...", "A verdade sobre...", "Pare de...", "Nunca mais..."
- Crie IMPOSSIBILIDADE de não ler
- Use variáveis da pesquisa de avatar quando disponíveis

### ATO 2 - ESCALADA DA DOR/OBSTÁCULO (Slides 2 até ${penultimateSlide})
**CONTEÚDO - INTEREST + DESIRE**

**Slides Iniciais (2-${firstContentEnd}):**
Use OBRIGATORIAMENTE elementos de:
- **Pontos de Identificação**: Situações ultra específicas que fazem pensar "sou eu!"
  - Exemplo: "Você senta no sofá com o prato na mão, sem nem lembrar o que cozinhou"
- **Intensificadores**: Amplifique emoção e vontade de continuar
  - Mistério: "Mas tem um detalhe que ninguém comenta..."
  - Desejo: "Todo mundo que aplica está vendo resultado"
  - Medo: "E se você não mudar isso, pode ser tarde"
  - Reconhecimento: "Não importa se já tentou de tudo"
  - Crença: "E não, não estou falando de jejum intermitente"
  - Autoridade: "Segundo a OMS, isso afeta 1 em cada 3"

**Slides do Meio (${middleContentStart}-${middleContentEnd}):**
Entregue VALOR PRÁTICO usando formatos:
- **Lista numerada**: "3 coisas que toda mulher espera" (explique benefícios de cada)
- **Checklist**: Lista simples de etapas
- **Dica Rápida**: Solução expressa para problema específico
- **Mini Tutorial**: (1) Qual problema, (2) O que fazer, (3) Como aplicar
- **Comparativo**: Antes e Depois / Com ou Sem
- **Lista com Argumentação Poderosa**: Nome + Definição + Transformação + Emoção

Use também:
- **Fatos Comprovados e Curiosos**: Dados absurdos, comparações incomuns, estudos recentes
- **Storytelling**: História pessoal ou de transformação
- **Provas**: Social (números), Autoridade (formação), Resultado (cases), Demonstração (ao vivo), Dados (estudos)
- **Disrupção**: Controvérsia, Quebra de Padrão ou Plot Twist

**Slide Penúltimo (${penultimateSlide}):**
- Criar urgência e antecipação para o CTA
- Intensificador final forte

### ATO 3 - RESOLUÇÃO/TRANSFORMAÇÃO (Slide ${count})
**CTA - AÇÃO CLARA**

Use 1-2 tipos de CTA:
- **CTA de Compartilhamento**: "Compartilhe com toda mulher que precisa perder 10kg em 21 dias"
- **CTA de Salvamento**: "Salva esse vídeo para se libertar das dores no pescoço"
- **CTA de Comentário**: "Comenta 'quero' que eu te mando o passo a passo"

---

## REQUISITOS DE STORYTELLING

1. **Fluxo Natural**: Cada slide deve fluir para o próximo (não pontos desconectados)
2. **Voz Consistente**: Mantenha tom e personalidade do público-alvo
3. **Tensão e Resolução**: Crie expectativa e entregue valor
4. **Gancho Implícito**: Cada slide termina fazendo querer ver o próximo ("e então...")

---

## FORMATAÇÃO

- Use Markdown para ênfase: **bold** para destaque, *italic* para nuance
- Headers (# ou ##) APENAS no slide COVER (primeiro slide) - demais slides devem usar texto normal (parágrafos)
- Texto conciso e punchy (tweet-length por slide)
- Use bullet points APENAS quando essencial (listas de 2-4 items máximo)
- Evite excesso de formatação (low cognitive load)

### FORMATAÇÃO POR TIPO DE SLIDE

**SLIDE COVER (Slide 1):**
- Formato: # Título Principal + parágrafo de gancho em texto normal
- Exemplo:
  # O Segredo das Vendas High-Ticket

  Não é o que você **pensa**.

**SLIDES DE CONTEÚDO (Slides 2-${penultimateSlide}):**
- Formato: Texto normal (parágrafos), SEM headers (#)
- Use **bold** para ênfase, listas apenas quando necessário
- Cada slide = 1 insight ou ponto de transformação
- Exemplo:
  A maioria foca no pitch.

  Mas os melhores vendedores focam no **ambiente**.

**SLIDE CTA (Slide ${count}):**
- Formato: Texto normal, SEM headers
- Call-to-action claro e conectado ao desejo construído
- Exemplo:
  Siga para mais estratégias de vendas high-ticket.

---

## PROCESSO DE CRIAÇÃO

### ETAPA 1: ANÁLISE PRÉ-CRIAÇÃO
Antes de criar, analise:
1. Qual é o **desejo principal** do avatar sobre este tema?
2. Qual **dor específica** isso resolve?
3. Qual **crença popular** posso desafiar?
4. Que **informação nova/contraditória** posso trazer?
5. Qual **moeda social** isso ativa?
6. Como tornar isso **ultra específico** (pontos de identificação)?

### ETAPA 2: CONSTRUÇÃO DO COVER
1. Escolha 2-3 Gatilhos da Atenção
2. Use variáveis da pesquisa para personalizar
3. Teste mental: "É impossível NÃO clicar?"

### ETAPA 3: DESENVOLVIMENTO DO CONTEÚDO
1. Slides iniciais: Pontos de identificação + Intensificadores
2. Slides do meio: Valor prático (use formatos listados)
3. Sempre conecte com moeda social
4. Use storytelling quando relevante
5. Adicione provas quando necessário
6. Penúltimo slide: Crie urgência máxima

### ETAPA 4: FINALIZAÇÃO COM CTA
1. Escolha tipo de CTA estrategicamente
2. Conecte com o desejo construído
3. Facilite a ação

### ETAPA 5: REVISÃO FINAL
Checklist obrigatório:
- Tem informação nova/contraditória?
- Baixo carregamento cognitivo? (linguagem simples)
- Ativa moeda social? (qual categoria?)
- Usa brevidade inteligente?
- Pontos de identificação são ULTRA específicos?
- Tem valor prático claro?
- Cada slide flui naturalmente pro próximo?
- Cover é irresistível?
- CTA está conectado com o desejo?

---

## AVISOS CRÍTICOS

1. **NUNCA invente fatos, estudos ou dados**. Use apenas se tiver certeza ou marque como exemplo ilustrativo.
2. **SEMPRE priorize especificidade sobre generalização**: "Casais que dormem brigados" > "Casais que brigam muito"
3. **Moeda social é OBRIGATÓRIA**. Se o público não sentir vontade de compartilhar, refaça.
4. **Brevidade inteligente não é resumir mal**. É transmitir o mesmo valor com menos palavras.
5. **Cada slide deve fazer a pessoa querer ver o próximo**. Se não cria essa tensão, está incompleto.
6. **O Cover decide se as pessoas vão parar**. Se não usar pelo menos 2 gatilhos fortes, refaça.
7. **Valor prático é entregável**. A pessoa precisa poder FAZER algo com a informação.
8. **CTAs genéricos não funcionam**. Conecte com o desejo/dor específica do carrossel.

---

Crie exatamente ${count} slides sobre "${topicDisplay}" seguindo TODAS as diretrizes acima.
${hasDocument ? 'IMPORTANTE: Use EXCLUSIVAMENTE as informações do documento/fonte fornecido como base para o conteúdo. Não invente dados ou fatos que não estejam na fonte.\n' : ''}

## FORMATO DO CARROSSEL

Antes de gerar os slides, classifique o carrossel em "format":
- **"list"**: se o carrossel apresenta itens numerados (ex: "3 erros que…", "5 dicas para…", "7 coisas que…").
- **"narrative"**: caso contrário (explicação, história, argumentação contínua).

## REGRAS DE IMAGEM (campo needsImage)

Aplique estas regras ao marcar needsImage em cada slide:
- SEMPRE true no slide COVER (primeiro).
- SEMPRE false no slide CTA (último).
- Se format = "list": true em todos os slides que apresentam um item numerado; false nos demais slides do meio.
- Se format = "narrative": true em aproximadamente metade dos slides CONTENT, distribuídos de forma equilibrada com leve peso no final. Referência para 10 slides: capa, 3, 5, 7, 8. Escale proporcionalmente para outras contagens.

Para cada slide com needsImage=true, escreva um suggestedImagePrompt em inglês descrevendo UMA CENA ESPECÍFICA, nunca um conceito. Caso contrário, retorne string vazia.

Escolha o registro conforme o slide:
- CENA COM PESSOAS — quando há um humano afetado (alguém perdendo, decidindo, sendo substituído). Mostre quem é, o que faz e o que sente. Registro mais forte para a capa.
- METÁFORA CONCRETA — quando a ideia é abstrata ou quando mostrar rosto humano enfraqueceria. Use um objeto concreto em tensão: a cadeira vazia, os crachás sobre a mesa, a fila que acabou.

Toda imagem precisa de: (1) sujeito concreto e nomeável, (2) ação ou estado em curso, (3) carga emocional explícita, (4) enquadramento e luz.

NUNCA faça da tela, do gráfico, do dashboard ou da interface o SUJEITO da imagem — podem aparecer como adereço dentro de uma cena, nunca como assunto. NUNCA use: "digital transformation concept", "modern office setting", "business illustration", mão robótica isolada, ícones flutuantes, pessoa sorrindo genericamente para a câmera. Nunca inclua texto, letras ou números na imagem.

Use de 20 a 50 palavras. Especificidade vence brevidade.

Exemplos:
FRACO: "A futuristic robot hand replacing a human hand on a business desk"
FORTE: "A humanoid robot in a business suit hands a cardboard box of belongings to a stunned office worker who won't meet its eyes. Empty desks receding behind them. Cold light."

FRACO: "AI chatbot interface on a smartphone screen replacing a customer service agent"
FORTE: "An abandoned call center headset resting on a desk, cord still coiled, dozens of identical empty workstations stretching into the dark behind it."

FRACO: "AI automating financial spreadsheets and accounting tasks, digital transformation concept"
FORTE: "A bookkeeper in her fifties watches a screen fill with completed ledgers faster than she can read, her pen frozen above an untouched notepad."

Retorne estritamente no formato JSON especificado.
`;
};

/**
 * NOTE: buildLessonPrompt was removed - Lesson style has been deprecated.
 */

// NOTE: buildLessonPrompt was removed — Lesson carousel style has been deprecated.

/**
 * Generates carousel content using the backend AI service.
 * Returns slides plus Conteúdo Notável metadata (analysis + checklist).
 */
export const generateCarouselContent = async (
  topic: string,
  count: number = 7,
  modelName: string = TEXT_MODEL_PRO,
  document?: UploadedDocument,
  language: Language = 'pt-BR',
  avatarContext?: string,
  save?: CarouselGenerationSaveOptions,
): Promise<CarouselGenerationResult> => {
  let resolvedTopic = topic;
  let resolvedDocument = document;
  let youtubeUrlsForModel: string[] = [];

  const youtubeUrls = extractYouTubeUrls(topic).map(normalizeYouTubeUrl);

  if ((resolvedDocument?.type === 'pdf' || resolvedDocument?.type === 'image') && (!resolvedDocument.base64 || !resolvedDocument.mimeType)) {
    throw new Error('Falha ao processar arquivo: dados visuais ausentes (base64/mimeType). Tente enviar o arquivo novamente.');
  }

  if (youtubeUrls.length > 0) {
    youtubeUrlsForModel = youtubeUrls;
    resolvedTopic = topic.replace(youtubeUrls[0], '').trim() || 'Vídeo do YouTube fornecido';
  }

  // If the user pasted an Instagram URL, scrape it and inject as source context.
  // Note: resolvedDocument can have empty content for PDF (uses base64 visual)
  const hasValidDocument = resolvedDocument && (resolvedDocument.content || resolvedDocument.base64);
  if (!hasValidDocument && youtubeUrlsForModel.length === 0) {
    const instagramUrls = extractInstagramUrls(topic);
    if (instagramUrls.length > 0) {
      try {
        const postData = await scrapeInstagramPost(instagramUrls[0]);
        const instagramContent = formatInstagramContentForAI(postData);
        resolvedDocument = {
          name: 'instagram-source.txt',
          type: 'txt',
          content: instagramContent,
          size: instagramContent.length,
        };

        // Keep only user intent/context text, removing URL noise from title/topic.
        resolvedTopic = topic.replace(instagramUrls[0], '').trim() || 'Post do Instagram fornecido';
      } catch (error) {
        // Non-blocking fallback: if scraping fails, proceed with plain topic text.
        // Instagram scrape failed - fallback to raw topic text
      }
    }
  }

  const prompt = buildCarouselPrompt(resolvedTopic, count, resolvedDocument, language, avatarContext);

  const response = await apiPost<{
    format?: 'narrative' | 'list';
    slides: GeneratedSlideSchema[];
    analise_pre_criacao?: GeneratedCarouselResponse['analise_pre_criacao'];
    checklist_validacao?: GeneratedCarouselResponse['checklist_validacao'];
    model: string;
    durationMs: number;
    savedProject?: { id: string; name: string } | null;
  }>('/api/ai/generate', {
    topic: resolvedTopic,
    slideCount: count,
    model: modelName,
    prompt,
    youtubeUrls: youtubeUrlsForModel,
    document: resolvedDocument,
    ...(save ? { save: { ...save, language: save.language ?? language } } : {}),
  });

  const slides = (response.slides || []).map((s) => ({
    // Prefer the server-assigned id so client memory and the persisted
    // project share the same slide ids. Without this, reloading the project
    // from Supabase yields ids the editor doesn't know about and the
    // sidebar behaves as if no slide is active.
    id: s.id || generateId(),
    type: (s.type as SlideType) || SlideType.CONTENT,
    content: s.content,
    showImage: s.needsImage,
    imagePrompt: s.suggestedImagePrompt,
    imageUrl: undefined,
    imageScale: 50,
    overlayImage: true,
    // Conteúdo Notável metadata
    gatilhosUsados: s.gatilhos_usados,
    elementosUsados: s.elementos_usados,
    tipoCta: s.tipo_cta,
  }));

  const format = response.format ?? 'narrative';
  const normalizedSlides = applyImageDistributionRules(slides, format);

  const analisePreCriacao: AnalisePreCriacao = response.analise_pre_criacao
    ? {
      desejoPrincipal: response.analise_pre_criacao.desejo_principal || '',
      dorEspecifica: response.analise_pre_criacao.dor_especifica || '',
      crencaDesafiada: response.analise_pre_criacao.crenca_desafiada || '',
      informacaoNova: response.analise_pre_criacao.informacao_nova || '',
      moedaSocialAtivada: response.analise_pre_criacao.moeda_social_ativada || '',
      pontosIdentificacao: response.analise_pre_criacao.pontos_identificacao || [],
    }
    : {
      desejoPrincipal: '',
      dorEspecifica: '',
      crencaDesafiada: '',
      informacaoNova: '',
      moedaSocialAtivada: '',
      pontosIdentificacao: [],
    };

  const checklistValidacao: ChecklistValidacao = response.checklist_validacao
    ? {
      informacaoNova: response.checklist_validacao.informacao_nova ?? false,
      baixoCarregamentoCognitivo: response.checklist_validacao.baixo_carregamento_cognitivo ?? false,
      moedaSocial: response.checklist_validacao.moeda_social ?? false,
      brevidadeInteligente: response.checklist_validacao.brevidade_inteligente ?? false,
      pontosIdentificacaoEspecificos: response.checklist_validacao.pontos_identificacao_especificos ?? false,
      valorPratico: response.checklist_validacao.valor_pratico ?? false,
      fluxoNatural: response.checklist_validacao.fluxo_natural ?? false,
      coverIrresistivel: response.checklist_validacao.cover_irresistivel ?? false,
      ctaConectado: response.checklist_validacao.cta_conectado ?? false,
    }
    : {
      informacaoNova: false,
      baixoCarregamentoCognitivo: false,
      moedaSocial: false,
      brevidadeInteligente: false,
      pontosIdentificacaoEspecificos: false,
      valorPratico: false,
      fluxoNatural: false,
      coverIrresistivel: false,
      ctaConectado: false,
    };

  return {
    slides: normalizedSlides,
    format,
    analisePreCriacao,
    checklistValidacao,
    savedProject: response.savedProject ?? undefined,
  };
};

// ============================================================================
// CONTENT REFINEMENT (via Backend)
// ============================================================================

export const refineCarouselContent = async (
  slides: Slide[],
  feedback: string,
  slideIndex?: number,
  modelName: string = TEXT_MODEL_PRO,
  language: Language = 'pt-BR'
): Promise<Slide[]> => {
  const isGlobal = slideIndex === undefined;
  const langInstruction = LANGUAGE_INSTRUCTIONS[language] || LANGUAGE_INSTRUCTIONS['pt-BR'];

  const formatSlide = (s: Slide, i: number) =>
    `Slide ${i + 1} (${s.type}):\n${s.content}`;

  const currentContent = isGlobal
    ? slides.map((s, i) => formatSlide(s, i)).join('\n\n')
    : formatSlide(slides[slideIndex!], slideIndex!);

  const prompt = isGlobal
    ? `${langInstruction}

You are refining an Instagram carousel created with the "Conteúdo Notável" methodology. Apply the following feedback to ALL slides.

FEEDBACK: "${feedback}"

CURRENT CAROUSEL CONTENT:
${currentContent}

Requirements:
1. Apply the feedback consistently across all ${slides.length} slides
2. Maintain each slide's type (COVER, CONTENT, CTA)
3. Keep the same number of slides (${slides.length})
4. Preserve Markdown formatting (# headers, **bold**, etc.)
5. Keep suggestedImagePrompt relevant to the new content
6. PRESERVE the 4 principles of Conteúdo Notável: Informação Nova, Baixo Carregamento Cognitivo, Moeda Social, Brevidade Inteligente
7. PRESERVE the 3-Act Storytelling structure and Attention Triggers (Gatilhos da Atenção)
8. Maintain storytelling coherence and natural flow between slides
9. ALL content MUST remain in the specified language

Return strictly JSON with the refined slides.`
    : `${langInstruction}

You are refining a single slide from an Instagram carousel created with the "Conteúdo Notável" methodology. Apply the following feedback to this specific slide only.

FEEDBACK: "${feedback}"

CURRENT SLIDE CONTENT:
${currentContent}

Requirements:
1. Apply the feedback to this slide
2. Maintain the slide type: ${slides[slideIndex!].type}
3. Preserve Markdown formatting
4. Update suggestedImagePrompt if content changed significantly
5. Maintain Conteúdo Notável principles: Brevidade Inteligente, Baixo Carregamento Cognitivo, Moeda Social
6. The content MUST remain in the specified language

Return strictly JSON with ONE refined slide.`;

  const response = await apiPost<{
    result: any;
    model: string;
    durationMs: number;
  }>('/api/ai/refine', {
    prompt,
    isGlobal,
    model: modelName,
  });

  const json = response.result;

  if (isGlobal) {
    const refinedSlides = json.slides || [];
    return slides.map((original, i) => ({
      ...original,
      type: refinedSlides[i]?.type || original.type,
      content: refinedSlides[i]?.content || original.content,
      showImage: refinedSlides[i]?.needsImage ?? original.showImage,
      imagePrompt: refinedSlides[i]?.suggestedImagePrompt || original.imagePrompt,
    }));
  } else {
    return slides.map((original, i) => {
      if (i !== slideIndex) return original;
      return {
        ...original,
        type: json.type || original.type,
        content: json.content || original.content,
        showImage: json.needsImage ?? original.showImage,
        imagePrompt: json.suggestedImagePrompt || original.imagePrompt,
      };
    });
  }
};

// ============================================================================
// IMAGE GENERATION (via Backend)
// ============================================================================

export const DEFAULT_IMAGE_STYLE = 'Cinematic editorial photography, dramatic directional lighting, rich contrast, shallow depth of field, muted color grade, 35mm.';
export const DEFAULT_BACKGROUND_IMAGE_STYLE = 'Soft atmospheric photography, diffused even lighting, low contrast, muted desaturated tones, gentle depth of field.';

export const generateSlideImage = async (
  prompt: string,
  aspectRatio: AspectRatio,
  modelName: string = IMAGE_MODEL_FLASH,
  globalStyle: string = DEFAULT_IMAGE_STYLE,
  projectId?: string,
  slideId?: string,
  imageType?: 'slide' | 'background'
): Promise<string> => {
  const apiAspectRatio = getApiAspectRatio(aspectRatio);
  const trimmedStyle = globalStyle.trim();
  // Um estilo igual ao padrão de slide significa "não customizado": nesse caso a
  // imagem de fundo deve usar o padrão suave, não o dramático dos slides.
  const isCustomStyle = trimmedStyle !== '' && trimmedStyle !== DEFAULT_IMAGE_STYLE;
  const defaultStyle = imageType === 'background' ? DEFAULT_BACKGROUND_IMAGE_STYLE : DEFAULT_IMAGE_STYLE;
  const stylePrefix = isCustomStyle ? trimmedStyle : defaultStyle;

  const response = await apiPost<{
    imageUrl?: string;     // Storage URL (when projectId/slideId provided)
    imageBase64?: string;  // Fallback base64
    mimeType: string;
    model: string;
    durationMs: number;
  }>('/api/gemini/generate-image', {
    prompt: `${stylePrefix} ${prompt}`,
    aspectRatio: apiAspectRatio,
    model: modelName,
    projectId,
    slideId,
    imageType: imageType || 'slide',
  });

  // Prefer Storage URL, fallback to data URI
  if (response.imageUrl) {
    return response.imageUrl;
  }
  return `data:${response.mimeType || 'image/png'};base64,${response.imageBase64}`;
};

// ============================================================================
// IMAGE STYLIZATION (via Backend)
// ============================================================================

export const stylizeImage = async (
  stylePrompt: string,
  apiAspectRatio: string,
  modelName: string = IMAGE_MODEL_PRO,
  projectId?: string,
  slideId?: string,
  source?: { imageUrl: string } | { imageBase64: string; mimeType: string },
): Promise<string> => {
  const payload: Record<string, any> = {
    stylePrompt,
    aspectRatio: apiAspectRatio,
    model: modelName,
    projectId,
    slideId,
  };

  if (source && 'imageUrl' in source) {
    payload.imageUrl = source.imageUrl;
  } else if (source && 'imageBase64' in source) {
    payload.imageBase64 = source.imageBase64;
    payload.mimeType = source.mimeType;
  }

  const response = await apiPost<{
    imageUrl?: string;
    imageBase64?: string;
    mimeType: string;
    model: string;
    durationMs: number;
  }>('/api/gemini/stylize-image', payload);

  if (response.imageUrl) {
    return response.imageUrl;
  }
  return `data:${response.mimeType || 'image/png'};base64,${response.imageBase64}`;
};

// ============================================================================
// IMAGE EDITING (via Backend)
// ============================================================================

export const editImage = async (
  editPrompt: string,
  apiAspectRatio: string,
  modelName: string = IMAGE_MODEL_PRO,
  projectId?: string,
  slideId?: string,
  source?: { imageUrl: string } | { imageBase64: string; mimeType: string },
): Promise<string> => {
  const payload: Record<string, any> = {
    editPrompt,
    aspectRatio: apiAspectRatio,
    model: modelName,
    projectId,
    slideId,
  };

  if (source && 'imageUrl' in source) {
    payload.imageUrl = source.imageUrl;
  } else if (source && 'imageBase64' in source) {
    payload.imageBase64 = source.imageBase64;
    payload.mimeType = source.mimeType;
  }

  const response = await apiPost<{
    imageUrl?: string;
    imageBase64?: string;
    mimeType: string;
    model: string;
    durationMs: number;
  }>('/api/gemini/edit-image', payload);

  if (response.imageUrl) {
    return response.imageUrl;
  }
  return `data:${response.mimeType || 'image/png'};base64,${response.imageBase64}`;
};
